import {
  InvoicePreview,
  InvoicePreviewHandle,
} from "@jugl-web/domain-resources/invoices/components/InvoicePreview";
import { InvoiceModel } from "@jugl-web/rest-api";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import { TableCell } from "@jugl-web/ui-kit/data-display/Table";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { Menu, MenuItem } from "@jugl-web/ui-components/cross-platform/Menu";
import { useClipboard, useTranslations } from "@jugl-web/utils";
import { useRef, useState } from "react";
import { useInvoicesTableContext } from "../../context/useInvoicesTableContext";
import { useChangeInvoiceTypeOptions } from "../../hooks/useChangeInvoiceTypeOptions";
import { ReactComponent as DoubleCheckmarkIcon } from "./assets/double-checkmark.svg";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";
import { ReactComponent as LinkIcon } from "./assets/link.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { ReactComponent as SendIcon } from "./assets/send.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { InvoiceDeleteConfirmationDialog } from "./components/InvoiceDeleteConfirmationDialog";

interface ComponentProps {
  item: InvoiceModel;
}

export const InvoicesItemSettings = ({ item }: ComponentProps) => {
  const { onNavigate, availableActions } = useInvoicesTableContext();
  const { copyToClipboard } = useClipboard();
  const { t } = useTranslations();
  const [isPending, setIsPending] = useState(false);

  const { handleDeleteInvoice, addItem, entity, config, actions } =
    useInvoicesTableContext();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const dict = useInvoiceTypeToLabelDictionary();
  const type = dict[item.type];
  const invoicePreviewRef = useRef<InvoicePreviewHandle>(null);

  const changeInvoiceTypeOptions = useChangeInvoiceTypeOptions({
    type: actions.changeInvoiceType ? item.type : undefined,
  });

  const sections: MenuItem<unknown>[][] = [
    [
      {
        id: "edit",
        label: `${t({
          defaultMessage: "Edit",
          id: "common.edit",
        })} ${type}`,
        icon: <PenIcon />,
        isHidden: !availableActions.includes("update"),
      },
      {
        id: "1",
        label: t({
          defaultMessage: "Use as Template",
          id: "invoices.use-as-template",
        }),
        isHidden: true,
        icon: <PenIcon />,
      },
      {
        id: "download-pdf",
        label: t({
          defaultMessage: "Download PDF",
          id: "invoices.download-pdf",
        }),
        icon: <DownloadIcon />,
        isHidden: !availableActions.includes("download-pdf"),
      },
      ...changeInvoiceTypeOptions,
      {
        id: "3",
        label: t({
          defaultMessage: "Issue and Send to Customer",
          id: "invoices.issue-and-send-to-customer",
        }),
        isHidden: true,
        icon: <SendIcon />,
      },
      {
        id: "4",
        label: t({
          defaultMessage: "Issue Invoice",
          id: "invoices.issue-invoice",
        }),
        isHidden: true,
        icon: <DoubleCheckmarkIcon />,
      },
      {
        id: "delete",
        label: `${t({
          defaultMessage: "Delete",
          id: "common.delete",
        })} ${type}`,
        icon: <TrashIcon />,
        isHidden: !availableActions.includes("delete"),
      },
      {
        id: "copy-url",
        label: t({
          defaultMessage: "Copy URL",
          id: "invoices.copy-url",
        }),
        icon: <LinkIcon />,
        isHidden: !availableActions.includes("copy-url"),
      },
    ],
  ];

  const handleSelect = async (selected: MenuItem<unknown>) => {
    switch (selected.id) {
      case "edit":
        onNavigate?.("invoicesItemFormEdit", {
          id: item.id,
        });
        break;
      case "download-pdf":
        invoicePreviewRef.current?.download();
        break;
      case "delete":
        setIsDeleteDialogOpen(true);
        break;
      case "convert-to-proforma":
      case "convert-to-invoice":
      case "convert-to-purchase-order": {
        await actions
          .changeInvoiceType?.(selected.id, item.id)
          .then((newInvoice) => {
            addItem(newInvoice);
          });

        break;
      }
      case "copy-url":
        copyToClipboard(item.short_url);
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = async () => {
    setIsPending(true);
    await handleDeleteInvoice(item.id);
    setIsDeleteDialogOpen(false);
    setIsPending(false);
  };

  return (
    <TableCell>
      <Menu
        placement="right-end"
        autoClose
        onSelect={(_item, _event, close) => {
          handleSelect(_item);
          close();
        }}
        sections={sections}
        renderTrigger={({ Trigger, triggerRef }) => (
          <Trigger
            as={InteractiveContainer}
            ref={triggerRef}
            className="flex h-5 w-12 items-center justify-center"
          >
            <SettingsIcon />
          </Trigger>
        )}
      />

      <InvoicePreview
        entityName={entity?.name}
        invoice={item}
        ref={invoicePreviewRef}
        logo={config?.logo || entity?.img || ""}
        hidden
      />

      <InvoiceDeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        onRequestClose={() => setIsDeleteDialogOpen(false)}
        itemType={type}
        isDisabled={isPending}
      />
    </TableCell>
  );
};
